import type { ColumnFiltersState, RowModel } from '@tanstack/react-table'
import { applyCardFilters } from 'features/knowledgeDocumentViewer/components/advancedSearch/components/documentsCardView/documentCardFilterBar/applyCardFilters'
import type { CardFilterValues } from 'features/knowledgeDocumentViewer/components/advancedSearch/components/documentsCardView/documentCardFilterBar/types'
import {
  DocumentTableColumns,
  type DocumentTableRow,
} from 'features/knowledgeDocumentViewer/components/advancedSearch/components/documentsTableView/DocumentTableColumns'
import { useDocumentSearchResultFilters } from 'features/knowledgeDocumentViewer/components/advancedSearch/hooks/useDocumentSearchResultFilters'
import type {
  SearchHistoryItem,
  SearchStateUpdate,
  ViewMode,
} from 'features/knowledgeDocumentViewer/types'
import React, { useCallback, useEffect, useMemo, useState } from 'react'

export interface UseDocumentResultsFilterStateParams {
  documentResults: DocumentTableRow[]
  handleViewModeChange: (
    value: string | number,
    options?: { selectedDocumentIdWhenSwitchingToTable?: string | null }
  ) => void
  onSearchStateChange: (updates: SearchStateUpdate) => void
  searchStateFilters: SearchHistoryItem['searchState']['filters']
  searchQuery: string
  selectedDocumentId: string | null
  viewMode: ViewMode
}

export interface UseDocumentResultsFilterStateResult {
  activeIndex: number
  cardFilters: CardFilterValues
  commitCardFiltersToUrl: () => void
  filteredDocuments: DocumentTableRow[]
  handleCardFiltersChange: (next: CardFilterValues) => void
  handleTableFilteredDataChange: ReturnType<
    typeof useDocumentSearchResultFilters
  >['handleTableFilteredDataChange']
  hasActiveFilters: boolean
  onViewModeChange: (value: string | number) => void
  setActiveIndex: React.Dispatch<React.SetStateAction<number>>
  tableColumns: ReturnType<typeof DocumentTableColumns>
  tableFilteredCount: number
  tableHasActiveFilters: boolean
}

export function useDocumentResultsFilterState({
  documentResults,
  handleViewModeChange,
  onSearchStateChange,
  searchStateFilters,
  searchQuery,
  selectedDocumentId,
  viewMode,
}: UseDocumentResultsFilterStateParams): UseDocumentResultsFilterStateResult {
  const {
    cardFilters,
    setCardFilters,
    commitCardFiltersToUrl,
    handleTableFilteredDataChange: syncTableFiltersToSearchState,
  } = useDocumentSearchResultFilters({
    searchStateFilters,
    onSearchStateChange,
    viewMode,
  })

  const [activeIndex, setActiveIndex] = useState(0)
  const [tableFilteredCount, setTableFilteredCount] = useState(
    documentResults.length
  )

  useEffect(() => {
    setActiveIndex(0)
  }, [searchQuery])

  useEffect(() => {
    setTableFilteredCount(documentResults.length)
  }, [documentResults])

  const filteredDocuments = useMemo(
    () => applyCardFilters(documentResults, cardFilters),
    [documentResults, cardFilters]
  )

  const hasActiveFilters = useMemo(
    () =>
      Object.values(cardFilters).some((v) =>
        Array.isArray(v) ? v.length > 0 : typeof v === 'string' && v.length > 0
      ),
    [cardFilters]
  )

  const tableHasActiveFilters = useMemo(
    () =>
      (searchStateFilters ?? []).some(({ value }) =>
        Array.isArray(value)
          ? value.length > 0
          : typeof value === 'string' && value.length > 0
      ),
    [searchStateFilters]
  )

  const handleTableFilteredDataChange = useCallback(
    (
      filtered: RowModel<DocumentTableRow>,
      globalFilter: unknown,
      columnFilters: ColumnFiltersState
    ) => {
      setTableFilteredCount(filtered.rows.length)
      syncTableFiltersToSearchState(filtered, globalFilter, columnFilters)
    },
    [syncTableFiltersToSearchState]
  )

  const handleCardFiltersChange = useCallback(
    (next: CardFilterValues) => {
      setCardFilters(next)
      setActiveIndex(0)
    },
    [setCardFilters]
  )

  const tableColumns = useMemo(() => DocumentTableColumns(), [])

  const onViewModeChange = useCallback(
    (value: string | number) => {
      if (value === 'card') {
        if (selectedDocumentId) {
          const index = filteredDocuments.findIndex(
            (result) => result.id === selectedDocumentId
          )
          setActiveIndex(index >= 0 ? index : 0)
        } else {
          setActiveIndex((prev) =>
            filteredDocuments.length > 0
              ? Math.min(prev, filteredDocuments.length - 1)
              : 0
          )
        }
      }
      handleViewModeChange(
        value,
        value === 'table'
          ? {
              selectedDocumentIdWhenSwitchingToTable:
                filteredDocuments[activeIndex]?.id ?? null,
            }
          : undefined
      )
    },
    [handleViewModeChange, filteredDocuments, activeIndex, selectedDocumentId]
  )

  return {
    activeIndex,
    cardFilters,
    commitCardFiltersToUrl,
    filteredDocuments,
    handleCardFiltersChange,
    handleTableFilteredDataChange,
    hasActiveFilters,
    onViewModeChange,
    setActiveIndex,
    tableColumns,
    tableFilteredCount,
    tableHasActiveFilters,
  }
}
